import QRCode from 'qrcode';
import { Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  ErrorState,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useLabActions, useSpecimenLabels } from '@/features/orders/api';
import type { SpecimenLabel } from '@/features/orders/types';
import { isApiError } from '@/lib/api';
import styles from '@/pages/queue/queue.module.css';

function LabelQr({ payload }: { payload: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(payload, { margin: 0, width: 64 })
      .then((d) => live && setSrc(d))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [payload]);
  return src ? <img src={src} width={64} height={64} alt="Label QR" /> : null;
}

function Label({ l }: { l: SpecimenLabel }) {
  return (
    <div className={styles.specimenLabel} aria-label={`Label ${l.labelId}`}>
      <LabelQr payload={l.qr} />
      <div>
        <strong>{l.patientName}</strong>
        <br />
        IC …{l.icLast4 ?? '—'}
        {l.dateOfBirth ? ` · DOB ${l.dateOfBirth}` : ''}
        <br />
        {l.tests.join(', ')}
        <br />
        <strong>{l.tube}</strong>
        <br />
        {new Date(l.takenAt).toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}{' '}
        · {l.collectorInitials}
        <br />
        <span className="mono">{l.labelId}</span>
      </div>
    </div>
  );
}

/** Specimen labels (artifact #114): one per tube. */
export function LabelsDialog({
  orderId,
  onClose,
}: {
  orderId: string;
  onClose: () => void;
}) {
  const { data, isLoading, error, refetch } = useSpecimenLabels(orderId);
  const a = useLabActions();
  const toast = useToast();
  return (
    <Dialog
      open
      wide
      title="Specimen labels"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            icon={<Printer size={16} />}
            loading={a.labelsPrinted.isPending}
            onClick={() =>
              a.labelsPrinted
                .mutateAsync(orderId)
                .then(() => window.print())
                .catch((err: unknown) =>
                  toast.show({
                    tone: 'error',
                    title: 'Not done',
                    body: isApiError(err) ? err.message : undefined,
                    error: err,
                  }),
                )
            }
          >
            Print
          </Button>
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : error || !data ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className={styles.labelSheet}>
          {data.map((l) => (
            <Label key={l.labelId} l={l} />
          ))}
        </div>
      )}
    </Dialog>
  );
}
